import { useEffect, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import api from "../../utils/axios";

interface LevelItem {
  id: number;
  level: number;
  f1: number;
  f2: number;
  f3: number;
  f4: number;
  f5: number;
  f6: number;
  f7: number;
}

const BetCommissionLevel = () => {
  const [levels, setLevels] = useState<LevelItem[]>([]);
  const [editLevel, setEditLevel] = useState<LevelItem | null>(null);
  const [newLevel, setNewLevel] = useState<Partial<LevelItem>>({});

  /* ================= FETCH ================= */
  const fetchLevels = async () => {
    try {
      const res = await api.get("/getLevelBetCommission");
      setLevels(res.data.data || []);
    } catch {
      toast.error("Failed to fetch bet commission levels");
    }
  };

  useEffect(() => {
    fetchLevels();
  }, []);

  /* ================= HANDLERS ================= */
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    isEdit = false
  ) => {
    const { name, value } = e.target;

    if (isEdit && editLevel) {
      setEditLevel({ ...editLevel, [name]: Number(value) });
    } else {
      setNewLevel({ ...newLevel, [name]: Number(value) });
    }
  };

  const handleAddLevel = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post("/createLevelBetCommission", newLevel);
      toast.success("Level added successfully");
      setNewLevel({});
      fetchLevels();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to add level");
    }
  };

  const handleUpdateLevel = async () => {
    if (!editLevel) return;

    try {
      await api.post(
        `/updateLevelBetCommission/${editLevel.id}`,
        editLevel
      );
      toast.success("Level updated successfully");
      setEditLevel(null);
      fetchLevels();
    } catch {
      toast.error("Failed to update level");
    }
  };

  const handleDeleteLevel = async (id: number) => {
    if (!window.confirm("Delete this level?")) return;

    try {
      await api.delete(`/deleteLevelBetCommission/${id}`);
      toast.success("Level deleted successfully");
      fetchLevels();
    } catch {
      toast.error("Failed to delete level");
    }
  };

  const sortedLevels = [...levels].sort((a, b) => a.level - b.level);

  return (
    <div className="space-y-8">
      {/* ================= HEADER ================= */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800">
          Bet Commission Levels
        </h1>
        <p className="text-sm text-gray-500">
          Configure multi-level bet commissions (F1 → F7)
        </p>
      </div>

      {/* ================= ADD LEVEL ================= */}
      {/* <div className="bg-white border rounded-xl p-6 shadow-sm">
        <h2 className="text-lg font-semibold mb-4">
          Add New Level
        </h2>

        <form
          onSubmit={handleAddLevel}
          className="grid grid-cols-2 md:grid-cols-4 gap-4"
        >
          {["level", "f1", "f2", "f3", "f4", "f5", "f6", "f7"].map(
            (field) => (
              <input
                key={field}
                type="number"
                name={field}
                placeholder={field.toUpperCase()}
                value={(newLevel as any)[field] || ""}
                onChange={handleChange}
                className="border rounded-lg px-3 py-2"
              />
            )
          )}

          <div className="col-span-2 md:col-span-4">
            <button className="px-6 py-2 bg-gray-900 text-white rounded-lg">
              Add Level
            </button>
          </div>
        </form>
      </div> */}

      {/* ================= LEVEL TABLE ================= */}
      <div className="bg-white border rounded-xl shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              {["Level", "F1", "F2", "F3", "F4", "F5", "F6", "F7", "Actions"].map(
                (h) => (
                  <th key={h} className="px-4 py-3 text-left">
                    {h}
                  </th>
                )
              )}
            </tr>
          </thead>

          <tbody>
            {sortedLevels.length === 0 ? (
              <tr>
                <td
                  colSpan={9}
                  className="py-8 text-center text-gray-500"
                >
                  No levels found
                </td>
              </tr>
            ) : (
              sortedLevels.map((l) => (
                <tr key={l.id} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold">
                    L{l.level}
                  </td>

                  {["f1", "f2", "f3", "f4", "f5", "f6", "f7"].map(
                    (f) => (
                      <td key={f} className="px-4 py-3">
                        {editLevel?.id === l.id ? (
                          <input
                            type="number"
                            name={f}
                            value={(editLevel as any)[f]}
                            onChange={(e) =>
                              handleChange(e, true)
                            }
                            className="border rounded px-2 py-1 w-20"
                          />
                        ) : (
                          (l as any)[f]
                        )}
                      </td>
                    )
                  )}

                  <td className="px-4 py-3 flex gap-2">
                    {editLevel?.id === l.id ? (
                      <>
                        <button
                          onClick={handleUpdateLevel}
                          className="px-3 py-1 bg-green-600 text-white rounded text-xs"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditLevel(null)}
                          className="px-3 py-1 border rounded text-xs"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => setEditLevel(l)}
                          className="px-3 py-1 bg-yellow-500 text-white rounded text-xs"
                        >
                          Edit
                        </button>
                        {/* <button
                          onClick={() => handleDeleteLevel(l.level)}
                          className="px-3 py-1 bg-red-600 text-white rounded text-xs"
                        >
                          Delete
                        </button> */}
                      </>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <ToastContainer />
    </div>
  );
};

export default BetCommissionLevel;
